import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import HeartIcon from "../../icons/heart.icon";
import Trash2Icon from "../../icons/trash2.icon";
import CartQtyBtn from "./cartQtyBtn.comp";
import React from "react";
import Availability from "../products/productAvailability.comp";
import Link from "next/link";
import Image from "next/image";
import { CartItem } from "../../types/cart";
import { getProductUrl } from "../../core/productUrl.core";
import { useCartProductDisplayName } from "../../hooks/useCartProductDisplayName.hook";
import CartItemPriceDisplay from "./cartItemPriceDisplay.comp";
import { BundleItemDetails } from "./bundleItemDetails.comp";
import { useTranslation } from "../../core/translation.core";
import useCheckoutSession from "../common/useCheckoutSession.hook";
import useGetCartItemInventory from "../../hooks/useGetCartItemInventory.hook";
import { PointRedemptionBadge, PointRedemptionNoteForCartItem } from "./pointRedemptionDisplay.comp";
import { getCartItemMaxQuantity, isCartItemPointRedeemed } from "../../core/pointRedemption.core";
import { InsufficientStockErrorItem } from "../../core/insufficientStock.core";

export interface CartItemProps {
  item: CartItem;
  index: number;
  totalItems: number;
  onRemove: () => void;
  onUpdateQuantity: (quantity: number) => Promise<void>;
  allCartItems?: CartItem[];
  insufficientStockItem?: InsufficientStockErrorItem;
}

export function CartItemComp({
  item,
  index,
  totalItems,
  onRemove,
  onUpdateQuantity,
  allCartItems,
  insufficientStockItem,
}: CartItemProps) {
  const { t, msg } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [currentQuantity, setCurrentQuantity] = useState(item.quantity);

  const { data: checkoutSession } = useCheckoutSession(true);
  const selectedShipOptionId = checkoutSession?.shipOptionId;

  const invalidShipSelected = selectedShipOptionId && !item.product.validShipOptionIds?.includes(selectedShipOptionId);

  const isAddOnItem = !!item.addOnDiscount;
  const parentItem = isAddOnItem && allCartItems ? allCartItems.find((i) => i.id === item.attachCartItemId) : null;
  const inventory = useGetCartItemInventory(item);

  useEffect(() => {
    setCurrentQuantity(item.quantity);
  }, [item]);

  const handleQuantityChange = async (quantity: number) => {
    setIsUpdating(true);
    try {
      await onUpdateQuantity(quantity);
      setCurrentQuantity(quantity);
    } catch (error) {
      console.error("Error updating quantity:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const maxQuantity = getCartItemMaxQuantity(item, inventory);
  const highlightedMaxQuantity = insufficientStockItem?.availableQuantity ?? maxQuantity;

  useEffect(() => {
    if (isAddOnItem) return;
    if (maxQuantity === undefined) return;
    if (maxQuantity <= 0) return;
    if (item.quantity > maxQuantity && !isUpdating) {
      handleQuantityChange(maxQuantity);
    }
  }, [maxQuantity, isAddOnItem, item.quantity, isUpdating]);

  const handleWishlistToggle = () => {
    setIsWishlisted(!isWishlisted);
    toast.success(isWishlisted ? t(msg.product.removeFromCartSuccessfully) : t(msg.product.addToCartSuccessfully));
  };

  const itemTotal =
    item.itemTotal ??
    (isAddOnItem && item.discountPrice ? item.discountPrice * currentQuantity : item.finalPrice * currentQuantity);

  return (
    <div
      className={`py-6 px-6 
        ${index !== totalItems - 1 ? "border-b border-gray-100" : ""} 
        ${invalidShipSelected ? "opacity-35" : ""}
        ${insufficientStockItem ? "border-2 border-red-300 bg-red-50/40 rounded-lg my-2" : ""}
      `}
    >
      <div className="flex gap-6 items-start">
        <div className="relative w-24 h-24 flex-shrink-0">
          <Link href={getProductUrl(item.product)}>
            {item.product.featureMedia ? (
              <Image
                src={item.product.featureMedia.url}
                width={96}
                height={96}
                alt={item.product.name}
                className="object-contain"
                unoptimized
              />
            ) : (
              <Image src="/images/logo-square.webp" width={96} height={96} alt={item.product.name} className="object-contain" />
            )}
          </Link>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col md:flex-row items-start justify-between">
            <CartItemDetails
              item={item}
              isAddOnItem={isAddOnItem}
              parentItem={parentItem}
              currentQuantity={currentQuantity}
              inventory={inventory}
              onQuantityChange={handleQuantityChange}
              insufficientStockItem={insufficientStockItem}
              highlightedMaxQuantity={highlightedMaxQuantity}
            />

            <CartItemActions
              item={item}
              itemTotal={itemTotal}
              currentQuantity={currentQuantity}
              isWishlisted={isWishlisted}
              isUpdating={isUpdating}
              onWishlistToggle={handleWishlistToggle}
              onRemove={onRemove}
            />
          </div>
          <ApplyingDiscounts item={item} />
        </div>
      </div>
    </div>
  );
}

interface CartItemDetailsProps {
  item: CartItem;
  isAddOnItem: boolean;
  parentItem: CartItem | null | undefined;
  currentQuantity: number;
  inventory: number | undefined;
  onQuantityChange: (quantity: number) => Promise<void>;
  insufficientStockItem?: InsufficientStockErrorItem;
  highlightedMaxQuantity?: number;
}

function CartItemDetails({
  item,
  isAddOnItem,
  parentItem,
  currentQuantity,
  inventory,
  onQuantityChange,
  insufficientStockItem,
  highlightedMaxQuantity,
}: CartItemDetailsProps) {
  const { t, msg } = useTranslation();
  const { displayName, variantValue } = useCartProductDisplayName(item.product);

  const maxQuantity = highlightedMaxQuantity ?? getCartItemMaxQuantity(item, inventory);
  const isPointRedemptionItem = isCartItemPointRedeemed(item);
  const showStockWarning =
    insufficientStockItem !== undefined || (maxQuantity !== undefined && (maxQuantity <= 0 || currentQuantity > maxQuantity));

  return (
    <div className="flex-1 min-w-0 pr-4">
      <div className="flex flex-col items-start gap-2 mb-1">
        <h3 className="text-base font-medium leading-6 line-clamp-2">
          <Link href={getProductUrl(item.product)}>{displayName}</Link>
        </h3>
        {variantValue ? (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-400 rounded flex-shrink-0">
            {variantValue}
          </span>
        ) : null}
        {isAddOnItem && (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-white bg-red-500 rounded flex-shrink-0">
            {t(msg.addOnDiscount.badge)}
          </span>
        )}
        {isPointRedemptionItem ? <PointRedemptionBadge /> : null}
      </div>

      {isAddOnItem && parentItem && (
        <p className="text-xs text-gray-500 mb-2">
          {t(msg.addOnDiscount.attachedTo).replace("{productName}", parentItem.product.name)}
        </p>
      )}

      <div className="text-sm font-normal">
        <Availability product={item.product} />
        {isPointRedemptionItem ? <PointRedemptionNoteForCartItem item={item} quantity={currentQuantity} /> : null}
      </div>

      <BundleItemDetails item={item} />

      {showStockWarning && maxQuantity !== undefined ? (
        <p className="mt-2 text-sm font-medium text-red-600">
          {maxQuantity <= 0 ? t(msg.product.outOfStock) : t(msg.product.lessStock).replace("{available}", String(maxQuantity))}
        </p>
      ) : null}

      <div className="mt-6">
        {maxQuantity === undefined && !isAddOnItem ? (
          <div className="w-32 h-10 bg-gray-100 animate-pulse rounded-md" />
        ) : (
          <CartQtyBtn
            value={currentQuantity}
            onChange={onQuantityChange}
            min={1}
            max={maxQuantity}
            className="w-32"
            product={item.product}
          />
        )}
      </div>
    </div>
  );
}

interface CartItemActionsProps {
  item: CartItem;
  itemTotal: number;
  currentQuantity: number;
  isWishlisted: boolean;
  isUpdating: boolean;
  onWishlistToggle: () => void;
  onRemove: () => void;
}

function CartItemActions({
  item,
  itemTotal,
  currentQuantity,
  isWishlisted,
  isUpdating,
  onWishlistToggle,
  onRemove,
}: CartItemActionsProps) {
  const { t, msg } = useTranslation();
  return (
    <div className="flex flex-col items-end">
      <CartItemPriceDisplay item={item} itemTotal={itemTotal} quantity={currentQuantity} />

      <div className="flex items-center gap-4 mt-6">
        <button className="flex items-center text-gray-400 hover:text-red-500 transition-colors" onClick={onWishlistToggle}>
          {isWishlisted ? <HeartIcon className="w-5 h-5 fill-red-500 text-red-500" /> : <HeartIcon className="w-5 h-5" />}
          <span className="ml-1.5 text-sm">{isWishlisted ? t(msg.product.removeFromCart) : t(msg.product.addToWishlsit)}</span>
        </button>
        <button className="flex items-center text-gray-400 hover:text-gray-600" onClick={onRemove} disabled={isUpdating}>
          <Trash2Icon className="w-5 h-5" />
          <span className="ml-1.5 text-sm">{t(msg.product.remove)}</span>
        </button>
      </div>
    </div>
  );
}

function ApplyingDiscounts({ item }: { item: CartItem }) {
  if (!item.applyingDiscounts || item.applyingDiscounts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-end mt-2">
      {item.applyingDiscounts?.map((discount) => (
        <div className="bg-guru-milk-chocolate text-white px-2 rounded-md" key={discount.id}>
          {discount.name}
        </div>
      ))}
    </div>
  );
}
